import { pickBy, identity } from 'lodash'
import qs from 'qs'
import { libraryFilters, pdl } from '@edulastic/constants'
import { getFromSessionStorage } from '@edulastic/api/src/utils/Storage'

const { SMART_FILTERS } = libraryFilters
const {
  pearToAIGradeMap,
  pearToAISubjectMap,
  PDL_SUBJECTS_MAP,
  pdlToPearGradeMap,
} = pdl

export const BULK_CONVERSION_SESSION_KEY = 'pa.bulkConversionId'
export const BULK_CONVERSION_DISMISSED_KEY = 'pa.bulkConversionBannerDismissed'

export const TEST_LIBRARY_TABS = {
  MY_CONTENT: 'my-content',
  READY_TO_REVIEW: 'ready-to-review',
}

export function clearBulkConversionSession() {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    window.sessionStorage.removeItem(BULK_CONVERSION_SESSION_KEY)
  }
}

function readDismissedConversionId() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return ''
  }
  return window.sessionStorage.getItem(BULK_CONVERSION_DISMISSED_KEY) || ''
}

export function isBulkConversionBannerDismissed(conversionId) {
  if (conversionId == null || String(conversionId).trim() === '') {
    return false
  }
  return readDismissedConversionId() === String(conversionId).trim()
}

/** Remember user closed the summary so refresh and tab return do not re-show the bar. */
export function markBulkConversionBannerDismissed(conversionId) {
  if (
    conversionId == null ||
    String(conversionId).trim() === '' ||
    typeof window === 'undefined' ||
    !window.sessionStorage
  ) {
    return
  }
  const id = String(conversionId).trim()
  window.sessionStorage.setItem(BULK_CONVERSION_DISMISSED_KEY, id)
  clearBulkConversionSession()
}

export function persistBulkConversionHandoff(conversionId) {
  if (
    conversionId == null ||
    typeof window === 'undefined' ||
    !window.sessionStorage
  ) {
    return
  }
  window.sessionStorage.setItem(
    BULK_CONVERSION_SESSION_KEY,
    String(conversionId)
  )
}

/**
 * Read PDL bulk handoff from router state, with sessionStorage + URL tab fallback on refresh.
 */
export function parsePdlBulkHandoffFromLocation(location = {}) {
  const st = location.state || {}
  const searchParams = qs.parse(location.search || '', {
    ignoreQueryPrefix: true,
  })
  const onReadyTab = searchParams.tab === TEST_LIBRARY_TABS.READY_TO_REVIEW

  let bulkConversionId = st.bulkConversionId
  const recoveredFromSession = !bulkConversionId
  if (!bulkConversionId) {
    bulkConversionId =
      getFromSessionStorage(BULK_CONVERSION_SESSION_KEY) || null
  }

  if (bulkConversionId && isBulkConversionBannerDismissed(bulkConversionId)) {
    bulkConversionId = null
  }

  const fromBulkConvert =
    Boolean(st.fromBulkConvert) ||
    Boolean(bulkConversionId && (onReadyTab || recoveredFromSession))

  return { bulkConversionId, fromBulkConvert, onReadyTab }
}

/** Map flat Pear `conversion` fields → My Tests `grades` / `subject` filter values. */
export function defaultFiltersFromConversion(c) {
  const grades = []
  let pearGradeKey = null
  if (c.grade_code != null && String(c.grade_code).trim() !== '') {
    pearGradeKey = String(c.grade_code).trim()
  } else if (
    c.grade_level_id != null &&
    String(c.grade_level_id).trim() !== ''
  ) {
    const n = parseInt(String(c.grade_level_id).trim(), 10)
    if (!Number.isNaN(n)) pearGradeKey = pdlToPearGradeMap(n)
  }
  if (pearGradeKey) {
    const pa = pearToAIGradeMap(pearGradeKey)
    if (pa) grades.push(pa)
  }

  const subject = []
  let pdlEntry = null
  if (c.subject_code != null && String(c.subject_code).trim() !== '') {
    const code = String(c.subject_code).trim().toUpperCase()
    pdlEntry = Object.values(PDL_SUBJECTS_MAP).find((s) => s.code === code)
  } else if (c.subject_id != null && String(c.subject_id).trim() !== '') {
    const sid = String(c.subject_id).trim()
    pdlEntry = Object.values(PDL_SUBJECTS_MAP).find((s) => String(s.id) === sid)
  }
  if (pdlEntry) {
    const pa = pearToAISubjectMap(pdlEntry.description)
    if (pa) subject.push(pa)
  }

  return { grades, subject }
}

/**
 * Route to My Tests after PDL bulk upload. Pear sends a flat `conversion` object.
 * When review tabs are enabled, land on Ready to Review and persist conversion id in sessionStorage.
 */
export function buildMyContentRouteAfterPdlUpload(
  conversion,
  { reviewTabsEnabled = false } = {}
) {
  const { grades, subject } =
    conversion && typeof conversion === 'object'
      ? defaultFiltersFromConversion(conversion)
      : { grades: [], subject: [] }

  const query = pickBy(
    {
      filter: SMART_FILTERS.AUTHORED_BY_ME,
      page: 1,
      limit: 20,
      ...(reviewTabsEnabled ? { tab: TEST_LIBRARY_TABS.READY_TO_REVIEW } : {}),
      ...(grades.length ? { grades } : {}),
      ...(subject.length ? { subject } : {}),
    },
    identity
  )
  const search = `?${qs.stringify(query)}`

  const conversionId = conversion?.conversion_id

  return {
    pathname: '/author/tests',
    search,
    state: {
      fromBulkConvert: true,
      bulkConversionId: conversionId,
    },
  }
}
